import { useEffect, useState } from 'react';
import { KoobButton, KoobInputCheckbox, KoobModal } from '@koob/ui';
import { useTranslation } from 'react-i18next';
import KoobTitle from 'components/Koob/KoobTitle';
import KoobParagraph from 'components/Koob/KoobParagraph';

function MultiSelectLink({ select = 'all', onClick, disabled }) {
  const { t } = useTranslation('allotments');

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        'font-medium text-sm border-none bg-transparent p-0',
        !disabled
          ? 'cursor-pointer text-gray-500'
          : 'cursor-not-allowed text-gray-300',
      ].join(' ')}
    >
      {select === 'all' ? (
        <i className="far fa-check-square mr-1.5" />
      ) : (
        <i className="far fa-square mr-1.5" />
      )}
      {select === 'all' ? t('modal.selectAll') : t('modal.deselectAll')}
    </button>
  );
}

export default function NightRatesTargetOrganizationsModal({
  open,
  onClose,
  organizations,
  selectedOrganizationIds,
  setSelectedOrganizationIds,
  nightsCount,
}) {
  const { t } = useTranslation(['contracts', 'allotments']);

  const [selection, setSelection] = useState(selectedOrganizationIds);

  useEffect(() => {
    if (open) {
      setSelection(selectedOrganizationIds);
    }
  }, [open, selectedOrganizationIds]);

  const toggle = (id) => {
    setSelection((prev) =>
      prev.includes(id) ? prev.filter((o) => o !== id) : [...prev, id],
    );
  };

  const apply = () => {
    setSelectedOrganizationIds(selection);
    onClose();
  };

  return (
    <KoobModal open={open} setOpen={() => onClose()} widthClass="sm:max-w-xl">
      <div className="p-5 flex-col space-y-6">
        <div>
          <KoobTitle>{t('nightRatesEditor.organizationsModalTitle')}</KoobTitle>
          <KoobParagraph>
            {t('nightRatesEditor.organizationsModalSubtitle', {
              count: nightsCount,
            })}
          </KoobParagraph>
        </div>

        <div>
          <div className="flex space-x-3 items-center">
            <MultiSelectLink
              select="all"
              onClick={() =>
                setSelection(organizations.map((org) => org.value))
              }
              disabled={selection.length === organizations.length}
            />

            <MultiSelectLink
              select="none"
              onClick={() => setSelection([])}
              disabled={selection.length === 0}
            />
          </div>

          <div className="mt-5 grid grid-cols-2 gap-4">
            {organizations.map((org) => (
              <KoobInputCheckbox
                key={org.value}
                checked={selection.includes(org.value)}
                onCheckedChange={() => toggle(org.value)}
                testId={`target-org-${org.value}`}
              >
                <div className="leading-none text-sm text-gray-600 font-medium">
                  {org.isDmc ? (
                    <div className="bg-k-orange px-3 py-1.5 text-k-h rounded-md">
                      <i className="far fa-building mr-1.5" />
                      {org.label}
                    </div>
                  ) : (
                    <div>{org.label}</div>
                  )}
                </div>
              </KoobInputCheckbox>
            ))}
          </div>
        </div>

        <div className="text-xs text-gray-500">
          <i className="far fa-circle-info mr-1.5" />
          {t('nightRatesEditor.organizationsNotDistributed')}
        </div>

        <div className="flex justify-end space-x-3">
          <KoobButton variant="secondary" onClick={() => onClose()}>
            {t('misc:cancel')}
          </KoobButton>

          <KoobButton
            variant="primary"
            onClick={() => apply()}
            disabled={selection.length === 0}
            testId="apply-target-orgs"
          >
            {t('nightRatesEditor.organizationsModalApply', {
              count: selection.length,
            })}
          </KoobButton>
        </div>
      </div>
    </KoobModal>
  );
}
